import React, { useMemo } from 'react';
import Divider from 'flow/components/ui-components/Divider';
import useAppContext from 'flow/hooks/useAppContext';
import { SupervisorAgentCallPopover } from 'flow/components/pbx/supervisor/SupervisorAgentCallPopover';
import { useAppSelector } from 'flow/hooks/useRedux';
import {
    selectAllQueueMembers,
    selectQueues,
} from 'flow/selectors/queue-selectors';
import { QueueMemberQueue } from 'flow/types/Pbx';
import Queue, { QueueMember } from 'flow/types/Queue';
import Icon from 'flow/components/ui-components/Icon';
import { List } from 'immutable';
import { TypedMap } from 'flow/types/TypedMap';
import { ExtensionKey, QueueKey } from 'flow/types/EntityKeys';
import { QueueLoginList } from 'flow/types/Profile';

const getQueueMemberQueues = (
    profileNext: TypedMap<any> | undefined,
    queues: TypedMap<Record<`queue-${number}`, TypedMap<Queue>>>,
    allQueueMembers: TypedMap<Record<QueueKey, List<TypedMap<QueueMember>>>>,
    extensionKey: ExtensionKey | undefined,
) => {
    if (profileNext && profileNext.get('queueLoginList')) {
        return profileNext
            .get('queueLoginList')
            .map((queueLogin: TypedMap<QueueLoginList>) => ({
                key: queueLogin.get('queueEntityKey'),
                description: queueLogin.get('queueDescription'),
                loggedIn: queueLogin.get('loggedIn'),
            }));
    } else {
        let queueList: List<QueueMemberQueue> = List();
        queues.forEach((queue: TypedMap<Queue>) => {
            const queueMembers = allQueueMembers.get(queue.get('key'));
            if (!queueMembers) {
                return;
            }
            queueMembers.forEach((member) => {
                if (member.get('extensionKey') === extensionKey) {
                    queueList = queueList.push({
                        key: queue.get('key'),
                        loggedIn: member.get('loggedIn'),
                        description: queue.get('description'),
                    });
                }
            });
        });
        return queueList;
    }
};

interface Props {
    extensionKey?: ExtensionKey;
    callStartTime?: number;
    callAnsweredTime?: number;
    via?: string;
    callNumber?: string;
    inConfirmedCall?: boolean;
    profileNext?: TypedMap<any>;
}

const AgentQueueStatus = ({
    extensionKey,
    callNumber,
    via,
    callStartTime,
    callAnsweredTime,
    inConfirmedCall,
    profileNext,
}: Props) => {
    const { i18next } = useAppContext();
    const timestamp = inConfirmedCall ? callAnsweredTime : callStartTime;

    const queues = useAppSelector(selectQueues);
    const allQueueMembers = useAppSelector(selectAllQueueMembers);

    const memoizedQueueMemberQueues = useMemo(() => {
        return getQueueMemberQueues(
            profileNext,
            queues,
            allQueueMembers,
            extensionKey,
        );
    }, [profileNext, queues, allQueueMembers, extensionKey]);

    const renderCallBox = () => (
        <React.Fragment>
            <div className="flex flex-col p-3 pl-1">
                <div className="ps-2 leading-normal font-bold">
                    {i18next.t('Supervisor.AgentRow.Tooltip.Label.Call')}
                </div>
                <SupervisorAgentCallPopover
                    number={callNumber}
                    queueName={via}
                    timestamp={timestamp}
                    inConfirmedCall={inConfirmedCall}
                />
            </div>
            <Divider />
        </React.Fragment>
    );

    return (
        <React.Fragment>
            {callNumber && via && renderCallBox()}
            <div className="flex flex-col px-3 py-3">
                <div className="leading-normal font-bold whitespace-nowrap">
                    {i18next.t(
                        'Supervisor.AgentRow.Tooltip.Label.Channelstatus',
                    )}
                </div>
                {memoizedQueueMemberQueues.map(
                    (queue: QueueMemberQueue, index: number) => {
                        const isLastItem =
                            index === memoizedQueueMemberQueues.size - 1;
                        const iconKey = queue.loggedIn
                            ? 'success'
                            : 'radio_button_unchecked';
                        return (
                            <React.Fragment key={queue.key}>
                                <div className="flex h-8 items-center">
                                    <Icon
                                        key={queue.key}
                                        iconKey={iconKey}
                                        className="me-2 size-4 fill-gray-400"
                                        size="1rem"
                                    />
                                    <span className="text-sm whitespace-nowrap">
                                        {queue.description}
                                    </span>
                                </div>
                                {!isLastItem && <Divider className="my-2" />}
                            </React.Fragment>
                        );
                    },
                )}
            </div>
        </React.Fragment>
    );
};

export default React.memo(AgentQueueStatus);
